import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../common/Modal';
import PillButton from '../common/button/PillButton';
import PillMenu from '../common/menu/PillMenu';
import { fetchUsageSummary, fetchUsageTransactions } from '../../api/subscription';
import Table from '../common/table/Table';
import Pagination from '../common/table/Pagination';
import { FormDate } from '../common/form';

import styles from './styles/UsageModal.module.css';

const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'transactions', label: 'Transactions' },
];

const formatDateTime = date => {
    try {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }).format(date);
    } catch (e) {
        const d = new Date(date);
        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    }
};

const UsageModal = ({ isOpen, onClose }) => {
    const [from, setFrom] = useState(() => new Date(Date.now() - 30*24*60*60*1000));
    const [to, setTo] = useState(() => new Date());
    const [tab, setTab] = useState('summary');
    const [summary, setSummary] = useState(null);
    const [showPerAgent, setShowPerAgent] = useState(false);
    const [tx, setTx] = useState({ rows: [], total: 0, page: 1, limit: 50 });

    const toExclusiveIso = d => {
        const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
        return end.toISOString();
    };

    const load = useCallback(async () => {
        const fromISO = new Date(from.getFullYear(), from.getMonth(), from.getDate()).toISOString();
        const toISO = toExclusiveIso(to);
        const [s, t] = await Promise.all([
            fetchUsageSummary(fromISO, toISO),
            fetchUsageTransactions({ from: fromISO, to: toISO, page: 1, limit: 50 }),
        ]);
        setSummary(s);
        setTx(t);
    }, [from, to]);

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, load]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Usage" isLarge>
            <div className={styles.filters}>
                <FormDate label="From" value={from} onChange={setFrom} onAdjustPeer={setTo} peerValue={to} isStart minDate={new Date(2000,0,1)} maxDate={new Date(2100,0,1)} />
                <FormDate label="To" value={to} onChange={setTo} onAdjustPeer={setFrom} peerValue={from} maxDate={new Date(2100,0,1)} minDate={new Date(2000,0,1)} />
            </div>

            <div className={styles.tabsWrapper}>
                <PillMenu tabs={tabs} activeTab={tab} onTabChange={setTab} />
            </div>

            {tab === 'summary' && (
                <div style={{ display: 'grid', gap: 12 }}>
                    {!summary ? (
                        <div>Loading...</div>
                    ) : (
                        <>
                            <div className={styles.card}>
                                <div className={styles.info}>
                                    <span className={styles.name}>User Chats</span>
                                    <div className={styles.creditsSplit}>
                                        <span>Total: {summary.userChats.totalCredits.toLocaleString()} credits</span>
                                    </div>
                                    <div className={styles.creditsSplit}>
                                        <span>Third-party tools: {summary.userChats.thirdPartyToolsCredits.toLocaleString()} credits</span>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.card}>
                                <div className={styles.info}>
                                    <div className={styles.agentChatsContainer}>
                                        <div className={styles.info}>
                                            <span className={styles.name}>Agent Chats</span>
                                            <div className={styles.creditsSplit}>
                                                <span>Total: {summary.agentChats.totalCredits.toLocaleString()} credits</span>
                                            </div>
                                        </div>
                                        <PillButton size="small" onClick={() => setShowPerAgent(v => !v)}>
                                            {showPerAgent ? 'Hide per agent' : 'Show per agent'}
                                        </PillButton>
                                    </div>
                                    {showPerAgent && (
                                        <div className={styles.perAgent}>
                                            <Table
                                                columns={[
                                                    { key: 'agent', title: 'Agent', dataIndex: 'agentName' },
                                                    { key: 'credits', title: 'Credits', dataIndex: 'credits', align: 'right', render: v => v.toLocaleString() },
                                                ]}
                                                rows={summary.agentChats.perAgent || []}
                                                keyField="agentId"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {tab === 'transactions' && (
                <>
                    <Table
                        columns={[
                            { key: 'date', title: 'Date', dataIndex: 'createdAt', render: v => formatDateTime(new Date(v)), style: { minWidth: tx.rows.length > 0 ? '8rem' : '0' } },
                            { key: 'context', title: 'Context', dataIndex: 'contextType', render: v => v === 'TOOL' ? 'Tool' : 'Message' },
                            { key: 'chat', title: 'Chat', dataIndex: 'chatType', render: v => v === 'AGENT' ? 'Agent' : 'User' },
                            { key: 'agent', title: 'Agent', dataIndex: 'agentName', render: v => v || '-', style: { minWidth: tx.rows.length > 0 ? '8rem' : '0' } },
                            { key: 'credits', title: 'Credits', dataIndex: 'credits', align: 'right', render: v => v.toLocaleString() },
                        ]}
                        rows={tx.rows}
                        keyField="id"
                    />
                    {tx.rows.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Pagination
                                page={tx.page}
                                limit={tx.limit}
                                total={tx.total}
                                onChange={async nextPage => {
                                    const fromISO = new Date(from.getFullYear(), from.getMonth(), from.getDate()).toISOString();
                                    const toISO = toExclusiveIso(to);
                                    const t = await fetchUsageTransactions({ from: fromISO, to: toISO, page: nextPage, limit: tx.limit });
                                    setTx(t);
                                }}
                            />
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default UsageModal;
